/*************************************************************************
 *  Copyright 2018 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 **************************************************************************/
/**
 * @file
 */

// eslint-disable-next-line import/named
import { discovery } from './Discovery';
import { logging } from './LoggingAPI';

const logger = logging.getLogger('Analytics');

export interface IAnalyticsConfig {
  getConsent?: () => boolean;
  getConsentAsync?: (category?: string) => Promise<boolean>;
  setContextData?: (contextData: IContextData) => void;
  unsetContextData?: () => void;
  userTags?: string[];
  addUserTags?: (...userTags: string[]) => void;
  setUserTags?: (userTags: string[]) => void;
  eventsOverrideConfig?: any;
}

export interface IContextData {
  content: any;
  custom: object;
  session: string;
  user: any;
  verb: string;
  source: string;
}

export interface IAnalytics {
  configure: (config: IAnalyticsConfig, sendEvent: (eventData: object, options?: object) => Promise<void>) => void;
  sendEvent: (eventData: object, options?: object) => Promise<void>;
  getEventSender: (id: string, eventConfigPath: string | null) => (eventData: object, options?: object) => void;
  getConsentCategory: (category: string) => boolean;
  getConsentCategoryAsync: (category: string) => Promise<boolean>;
  getConsent: () => boolean;
  setContextData: (contextData: IContextData) => void;
  unsetContextData: () => void;
  getEventsConfigPath: (id: string) => string | null;
}

interface AllowedListEvent {
  description?: string;
  apps?: string[];
  end_date?: string;
  required_sections?: Array<'content' | 'verb'>;
  additional_report_suites?: {
    qa?: Array<'adbnonacdcqa'>;
    prod?: Array<'adbnonacdcprod'>;
  };
  to_aep: boolean;
}

interface AllowedListConfig {
  allow: {
    [key: string]: AllowedListEvent;
  };
}

/**
 * @classdesc
 * This is a temporary shell implementation that will allow
 * dropins to begin coding their analytics.  Implementation to follow.
 * @class
 */
class Analytics implements IAnalytics {
  _maxInitialEvents = 50;
  _initialEvents = [];
  _config: IAnalyticsConfig;
  _initialized;
  _consent;
  _getConsent:(category?: string) => boolean;
  _getConsentAsync:(category?: string) => Promise<boolean>;
  _setUserTags: (tags: string[]) => void;
  _addUserTags: (...tags: string[]) => void;
  _setContextData;
  _unsetContextData;
  _dropinIdToRepoMap: Map<string, string> = new Map();
  _allowedListConfig: Record<string, any> = {};
  _pendingAllowedListFetches: Record<string, Promise<any>> = {};
  _overrideConfig: any | null;
  _registeredSendEvent: (eventData: object, options?: object) => Promise<void> | null;

  _ecid;

  _alloyOn = window.adobe_dc_sdk?.launchOptions?.alloyOn;

  /**
   * Configures the Analytics provider and then sends all initial events.
   * Once this method is called with both arguments, all subsequent calls will be ignored.
   * @method
   * @param {object} config -- the new configuration
   * @param {function} sendEvent -- the new sendEvent function
   * @public
   */
  configure(config: IAnalyticsConfig, sendEvent?: (eventData: object, options?: object) => Promise<void>) {
    if (!this._initialized) {
      this._initialized = !!(config && sendEvent);
      if (config) {
        this._config = config;
        if (typeof config.getConsent === 'function') {
          this._getConsent = config.getConsent;
        }
        if (typeof config.getConsentAsync === 'function') {
          this._getConsentAsync = config.getConsentAsync;
        }
        if (typeof config.setContextData === 'function') {
          this._setContextData = config.setContextData;
        }
        if (typeof config.unsetContextData === 'function') {
          this._unsetContextData = config.unsetContextData;
        }
        if (typeof config.addUserTags === 'function') {
          this._addUserTags = config.addUserTags;
        }
        if (typeof config.setUserTags === 'function') {
          this._setUserTags = config.setUserTags;
        }
        if (config.eventsOverrideConfig) {
          this._overrideConfig = config.eventsOverrideConfig;
        }
      }
      if (sendEvent) {
        this._registeredSendEvent = sendEvent;
        this._initialEvents.forEach(event => {
          // Adding overrideConfig to options if it exists,
          // We don't do this in the callback returned by the getEventSender because at the time there is possiblity
          // that configure has not been called yet (the case where we queue the events),
          // but we queue the events with options included.
          const overrideConfig = this._overrideConfig;
          const updatedOptions = {
            ...event.options,
            overrideConfig,
          };
          this.sendEventInternal(event.eventData, updatedOptions);
        });
        this._initialEvents.length = 0;
      }
    }
  }

  /**
   * Parses the version manifest to create a mapping of dropin/provider IDs to their deployment IDs
   * @method
   * @private
   */
  private _parseVersionManifest() {
    try {
      const versionManifest = window.adobe_dc_sdk?.cache?.version_manifest;
      if (!versionManifest) {
        return;
      }

      Object.entries(versionManifest).forEach(([deploymentId, entry]) => {
        if (!entry?.dropins && !entry?.providers) {
          return;
        }

        // Map each dropin ID to its deployment ID
        if (entry.dropins) {
          Object.keys(entry.dropins).forEach(dropinId => {
            this._dropinIdToRepoMap.set(dropinId, deploymentId);
          });
        }

        // Map each provider ID to its deployment ID
        if (entry.providers) {
          Object.keys(entry.providers).forEach(providerId => {
            this._dropinIdToRepoMap.set(providerId, deploymentId);
          });
        }
      });
    } catch (error) {
      logger.warn('Failed to parse version manifest', { error });
    }
  }

  /**
   * Sends the analytics event by just adding it to initial list
   * @method
   * @param {object} eventData -- payload of an analytics event
   * @returns {object} - a promise which resolves if adding to initial list is successful
   * @private
   */
  private sendEventInternal(eventData, options) {
    if (this._registeredSendEvent) {
      return this._registeredSendEvent(eventData, options);
    }
    // If the provider is not initialized, add the event to the initial list
    if (this._initialEvents.length < this._maxInitialEvents) {
      this._initialEvents.push({ eventData, options });
      return Promise.resolve();
    }
    logger.error('Reached maximum number of initial events');
    return Promise.resolve();
  }

  /**
   * Sends the analytics event by just adding it to initial list
   * @method
   * @param {object} eventData -- payload of an analytics event
   * @returns {object} - a promise which resolves if adding to initial list is successful
   * @public
   */
  sendEvent(eventData, options) {
    // The assumption here is that sendEvent should never be called directly
    // and will be deprecated. Marking the events as AA_DENY
    // to avoid sending them to AA
    eventData.AA_DENY = 'direct_call_analytics_send_event';
    return this.sendEventInternal(eventData, options);
  }

  /**
   * Fetches and reads an analytics config from a given path for a specified source
   * @method
   * @private
   * @param {string} configPath - Path to the config file
   * @param {string} sourceName - Name of the source
   * @returns {Promise<object>} - A promise that resolves to the fetched config
   */
  async _fetchAndReadConfig(configPath: string): Promise<any> {
    try {
      const compositeKey = this._getCompositeKey(configPath);

      // Start a new fetch and store the promise
      this._pendingAllowedListFetches[compositeKey] = (async () => {
        try {
          // Default to empty config. So in case we fail to fetch,
          // we still have an empty object and we won't keep retrying.
          this._allowedListConfig[compositeKey] = {
            allow: {},
          };
          const response = await fetch(configPath);
          if (response.ok) {
            const rawConfig = await response.json();
            const config = await this._readAllowedListEvents(rawConfig);
            if (config) {
              this._allowedListConfig[compositeKey] = config;
            }
          } else {
            logger.warn(`Failed to fetch allowed list config from ${configPath}`);
          }
          return this._allowedListConfig[compositeKey];
        } catch (error) {
          logger.warn(`Error fetching allowed list config: ${error.message}`);
          return {
            allow: {},
          };
        } finally {
          // Clear the pending fetch when done
          delete this._pendingAllowedListFetches[compositeKey];
        }
      })();
      return this._pendingAllowedListFetches[compositeKey];
    } catch (error) {
      logger.warn(`Error setting up fetch for allowed list config: ${error.message}`);
      return {
        allow: {},
      };
    }
  }

  /**
   * Reads and processes the allowed list events from a raw config
   * @method
   * @private
   * @param {object} rawConfig - The raw config object
   * @param {string} sourceName - Name of the source
   * @returns {Promise<object>} - A promise that resolves to the processed config
   */
  async _readAllowedListEvents(rawConfig: any): Promise<AllowedListConfig | null> {
    if (rawConfig == null || !rawConfig || typeof rawConfig !== 'object' || !('allow' in rawConfig) || typeof rawConfig.allow !== 'object') {
      logger.warn('Invalid config format received - missing required \'allow\' section');
      return null;
    }

    const config: AllowedListConfig = {
      allow: {},
    };

    // Process each event in the allow section
    Object.entries(rawConfig.allow).forEach(([eventName, eventConfig]) => {
      if (typeof eventConfig === 'object' && eventConfig !== null) {
        const typedEventConfig = eventConfig as Partial<AllowedListEvent>;

        const allowedEvent: AllowedListEvent = {
          ...(typedEventConfig.apps && { apps: Array.isArray(typedEventConfig.apps) ? typedEventConfig.apps : [] }),
          ...(typedEventConfig.end_date && { end_date: typedEventConfig.end_date }),
          ...(typedEventConfig.required_sections && {
            required_sections: Array.isArray(typedEventConfig.required_sections)
              ? typedEventConfig.required_sections
              : [],
          }),
          ...(typedEventConfig.additional_report_suites && {
            additional_report_suites: {
              ...(typedEventConfig.additional_report_suites.qa && {
                qa: Array.isArray(typedEventConfig.additional_report_suites.qa)
                  ? typedEventConfig.additional_report_suites.qa
                  : [],
              }),
              ...(typedEventConfig.additional_report_suites.prod && {
                prod: Array.isArray(typedEventConfig.additional_report_suites.prod)
                  ? typedEventConfig.additional_report_suites.prod
                  : [],
              }),
            },
          }),
          ...(typedEventConfig.to_aep ? { to_aep: typedEventConfig.to_aep } : { to_aep: false }),
        };

        config.allow[eventName] = allowedEvent;
      }
    });

    // Only return config if there are valid events
    if (Object.keys(config.allow).length > 0) {
      return config;
    }
    logger.warn(`No valid events found in config at ${rawConfig}`);
    return null;
  }

  /**
   * Creates a composite key for caching configs
   * @method
   * @private
   * @param {string} configPath - Path to the config
   * @returns {string} - The composite key
   */
  _getCompositeKey(configPath: string): string {
    return `${configPath}`;
  }

  /**
   * Checks if there is a valid config for config path
   * @method
   * @private
   * @param {string} configPath - Path to the config
   * @returns {boolean} - Whether there is a valid config
   */
  _hasValidConfig(configPath: string): boolean {
    const compositeKey = this._getCompositeKey(configPath);
    return !!this._allowedListConfig[compositeKey];
  }

  /**
   * Get a logger that will add name/version to all analytics requests
   * @method
   * @param {string} id -- id of the dropin (or provider).  To be used as source.name
   * @param {string|null} [eventsConfigPath=null] -- optional path to the event config file
   * @returns {function} - a logger function that wraps sendEvent
   * @public
   */
  getEventSender(id: string, eventsConfigPath: string | null = null) {
    if (!id) {
      throw ReferenceError('missing dropin id');
    }

    // Parse version manifest on first call if not already done
    if (this._dropinIdToRepoMap.size === 0) {
      this._parseVersionManifest();
    }

    // Load config immediately if eventsConfigPath is provided
    let fetchPromise: Promise<any> | null = null;
    if (eventsConfigPath) {
      const compositeKey = this._getCompositeKey(eventsConfigPath);

      // Only fetch if we don't already have a valid config for this source and config path combination
      // and there's no fetch in progress
      if (!this._hasValidConfig(eventsConfigPath) && !this._pendingAllowedListFetches[compositeKey]) {
        fetchPromise = this._fetchAndReadConfig(eventsConfigPath);
      } else if (this._pendingAllowedListFetches[compositeKey]) {
        // If a fetch is in progress, keep reference to the promise
        fetchPromise = this._pendingAllowedListFetches[compositeKey];
      }
    }

    return async (eventData: any, options: object = {}) => {
      if (!eventData.source) {
        eventData.source = {};
      }

      eventData.source.name = eventData.source.name || id;

      // Set source repo id using cached deployment ID if available, otherwise use provided id
      eventData.source.repoId = this._dropinIdToRepoMap.get(id) || null;

      // code defensively so that unit tests that don't load discovery won't fail
      if (!eventData.source.version && discovery.dropins[id]) {
        eventData.source.version = discovery.dropins[id].version;
      } else if (!eventData.source.version && discovery.providers[id]) {
        eventData.source.version = discovery.providers[id].version;
      }
      // Wait for the config to be loaded if we initiated a fetch
      if (fetchPromise) {
        await fetchPromise;
      }

      // Get the config for this source/path
      let allowedListConfig = null;
      if (eventsConfigPath) {
        const compositeKey = this._getCompositeKey(eventsConfigPath);
        allowedListConfig = this._allowedListConfig[compositeKey] || null;
      }

      const updatedOptions = {
        ...options,
        eventsConfigPath,
        allowedListConfig, // Include the allowedListConfig in options
      };
      return Promise.resolve(this.sendEventInternal(eventData, updatedOptions));
    };
  }

  /**
   * Retrieves the consent status for a given category in Alloy.
   *
   * @param {string} category - The category for which to retrieve the consent status.
   * @returns {boolean} - The consent status for the given category. If no consent data it will return true
   */
  getConsentCategory(category: string): boolean {
    const cookieOptanonConsent = window._satellite?.cookie?.get('OptanonConsent');
    const { adobePrivacy } = window;
    const activeGroups = adobePrivacy?.activeCookieGroups?.();
    const consent = (
      (
        activeGroups
        && activeGroups.indexOf(category) !== -1
      )
      // otherwise use the cookie
      || (
        cookieOptanonConsent
        && cookieOptanonConsent.indexOf(`${category}:1`) !== -1
      )
    );
    return consent !== undefined ? consent : true;
  }

  /**
   * Asynchronously retrieves the consent status for a given category.
   *
   * This method first checks if the `_getConsent` function is available and uses it to get the consent status.
   * If `_getConsent` is not available, it waits for the `__satelliteLoadedPromise` to resolve and then attempts
   * to retrieve the consent status from the `window._satellite` object.
   *
   * @param {string} category - The consent category to check.
   * @returns {Promise<boolean>} A promise that resolves to `true` if the consent is granted, or rejects with an error if not.
   * @throws {Error} Throws an error if `_getConsent` or the consent category promise fails, or if the consent retrieval method is invalid.
   */
  getConsentCategoryAsync(category: string) {
    if (this._getConsentAsync) {
      return this._getConsentAsync(category).then(() => true).catch(error => {
        throw new Error(`_getConsentAsync error: ${error}`);
      });
    }
    return window.__satelliteLoadedPromise.then(() => {
      const consent = window._satellite?.getVar('consent');
      if (typeof consent === 'function' && consent[category]?.then) {
        return consent[category].then(() => true).catch(error => {
          throw new Error(`consent category error: ${error}`);
        });
      }
      throw new Error('satellite getVar consent is not a function or consent category is not a promise');
    });
  }

  /**
   * Retrieves the consent for analytics.
   *
   * @returns {boolean} The consent status for analytics.
   */
  getConsent(): boolean {
    if (this._getConsent) {
      return this._getConsent();
    }
    return this.getConsentCategory('C0002');
  }

  async getECID() {
    if (!this._ecid) {
      try {
        await window.__satelliteLoadedPromise;
        if (this._alloyOn) {
          const result = await window.alloy_getIdentity;
          if (result?.identity?.ECID) {
            this._ecid = result.identity.ECID;
          }
        } else {
          this._ecid = window.Visitor.getInstance(window._satellite.getVar(
            'marketingCloudOrganizationID')).getMarketingCloudVisitorID();
        }
      } catch (e) {
        return this._ecid;
      }
    }
    return this._ecid;
  }

  /**
   * Sets custom context data for analytics.
   * @param { object } contextData - Payload of an analytics event
   * @param { object } contextData.content - content - Optional
   * @param { object } contextData.custom  - custom  - Optional
   * @param { object } contextData.session - session - Optional
   * @param { object } contextData.user    - user    - Optional
   * @param { object } contextData.verb    - verb    - Optional
   * @param { object } contextData.source  - source  - Optional
   */
  setContextData(contextData: IContextData) {
    if (this._setContextData) {
      this._setContextData(contextData);
    }
  }

  /**
   * Unsets custom context data for analytics.
   */
  unsetContextData() {
    if (this._unsetContextData) {
      this._unsetContextData();
    }
  }

  setUserTags(userTags: string[]) {
    // eslint-disable-next-line no-unused-expressions
    this._setUserTags?.(userTags);
  }

  addUserTags(...userTags: string[]) {
    // eslint-disable-next-line no-unused-expressions
    this._addUserTags?.(...userTags);
  }

  /**
   * Gets the path to event_config.json for a given dropin or provider ID
   * @method
   * @param {string} id - ID of the dropin or provider
   * @returns {string|null} Path to event_config.json or null if path cannot be determined
   * @public
   */
  getEventsConfigPath(id: string): string | null {
    try {
      let url: string | undefined;

      // Check dropins first
      if (discovery.dropins[id]?.url) {
        url = discovery.dropins[id].url;
      } else if (discovery.providers[id]?.url) {
        // Then check providers
        url = discovery.providers[id].url;
      }

      if (!url) {
        return null;
      }

      // Replace the last part of the URL path with event_config.json
      return url.replace(/\/[^/]+$/, '/event_config.json');
    } catch (error) {
      logger.error('Error determining event config path:', error);
      return null;
    }
  }
}
const analytics = new Analytics();

export { analytics };
export default Analytics;
